import { useEffect, useRef } from 'react'
import * as THREE from 'three'
import FOG from 'vanta/dist/vanta.fog.min'

import type { CSSProperties } from 'react'
import type { VantaFogEffect } from 'vanta/dist/vanta.fog.min'

/**
 * Volumetric fog background using Vanta.js + Three.js, tuned for the hub's
 * light parchment theme. Pure white smoke would be invisible on parchment, so
 * we use cool-grey values slightly darker than the background — visible but
 * subtle, preserving text contrast.
 *
 * Skips mounting when the user prefers reduced motion.
 * @param props.className - Optional CSS classes for the container
 * @param props.style - Optional inline styles
 */
export default function VantaFog({
  className = '',
  style,
}: {
  className?: string
  style?: CSSProperties
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const effectRef = useRef<VantaFogEffect | null>(null)

  useEffect(() => {
    if (!containerRef.current) return

    // Respect reduced-motion: don't spin up WebGL animation.
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduceMotion) return

    effectRef.current = FOG({
      el: containerRef.current,
      THREE,
      mouseControls: false,
      touchControls: false,
      gyroControls: false,
      minHeight: 200,
      minWidth: 200,
      // Cool-grey light smoke for the parchment background (see plan Part F).
      highlightColor: 0xd8d2c6,
      midtoneColor: 0xc4bcab,
      lowlightColor: 0xa89f8c,
      baseColor: 0xefe9df,
      blurFactor: 0.6,
      speed: 1.5,
      zoom: 0.8,
    })

    return () => {
      if (effectRef.current) {
        effectRef.current.destroy()
        effectRef.current = null
      }
    }
  }, [])

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 0,
        ...style,
      }}
      aria-hidden="true"
    />
  )
}
